import { useId, useMemo, useRef, useState, type RefObject } from "react";
import { CLOSE_OUT_OF_SCOPE_ACTIVITY_TYPES, type ImportRunRow, type ImportSummaryView, type ImportVariable } from "@gioia365/contracts";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { Checkbox } from "../design/components/Checkbox";
import { DataTable } from "../design/components/DataTable";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { StatusPill } from "../design/components/StatusPill";
import { messageForError } from "../i18n/errors";
import { formatCount } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { useAccess } from "../session/access";
import { anomalyGroups, anomalyName, anomalyReference, anomalyWhat, describeAnomaly, fieldName, type AnomalyGroup, type AnomalyLevel } from "./close-import-anomalies";
import { closeImportAnomaliesQuery } from "./close-import-queries";
import { formatDuration, runDuration, runTitle } from "./close-import-format";

// Il rapporto di un'esecuzione finita della pagina «Importazione da Close» (piano M2 Task 21, proposte 7–14 del 1° ottobre
// 2026): «Riepilogo» in quattro riquadri, «Utenti di Close», «Come passano le variabili» e «Da controllare». Mostra
// soltanto ciò che il server ha salvato alla fine dell'esecuzione: numeri, valori dei campi a scelta, nomi del team e degli
// utenti di Close, riferimenti tecnici di Close. Il testo libero non arriva mai: di quelle variabili si conta soltanto
// quanti Lead le hanno. I conteggi per utente di Close sono quelli di Close e la pagina lo dice.

type Props = { run: ImportRunRow; summary: ImportSummaryView };

/** Quanti valori di una variabile si vedono prima di «Mostra tutti». */
const VALUES_SHOWN = 10;

export function CloseImportReport({ run, summary }: Props) {
  const t = useT();
  const baseId = useId();
  const ids = { summary: `${baseId}-summary`, users: `${baseId}-users`, variables: `${baseId}-variables`, check: `${baseId}-check` };
  const jumps = [
    { id: ids.summary, label: t("closeImport.jump.summary") },
    { id: ids.users, label: t("closeImport.jump.users") },
    { id: ids.variables, label: t("closeImport.jump.variables") },
    { id: ids.check, label: t("closeImport.jump.check") },
  ];

  // Porta la parte scelta in cima e il fuoco sul suo titolo, così la tastiera prosegue da lì.
  function jump(id: string) {
    const heading = document.getElementById(id);
    heading?.scrollIntoView?.({ block: "start" });
    heading?.focus({ preventScroll: true });
  }

  return (
    <div className="g-ci__report">
      <div className="g-ci__jump" role="group" aria-label={t("closeImport.jump.label")}>
        <span className="g-ci__muted">{t("closeImport.jump.lead")}</span>
        {jumps.map((target) => (
          <Button key={target.id} variant="ghost" onClick={() => jump(target.id)}>
            {target.label}
          </Button>
        ))}
      </div>
      <SummarySection titleId={ids.summary} run={run} summary={summary} />
      <UsersSection titleId={ids.users} summary={summary} dryRun={run.mode === "dry_run"} />
      <VariablesSection titleId={ids.variables} summary={summary} />
      <CheckSection titleId={ids.check} run={run} summary={summary} />
    </div>
  );
}

function SectionTitle({ id, title, sub }: { id: string; title: string; sub: string }) {
  return (
    <div className="g-ci__set-title">
      <h2 id={id} tabIndex={-1}>
        {title}
      </h2>
      <p className="g-ci__muted">{sub}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Riepilogo

type Fact = { label: string; value: number; sub?: string };

function FactCard({ title, sub, facts }: { title: string; sub: string; facts: Fact[] }) {
  const { locale } = useLocale();
  return (
    <div className="g-ci__card">
      <h3>{title}</h3>
      <p className="g-ci__muted">{sub}</p>
      <dl className="g-ci__facts">
        {facts.map((fact) => (
          <div key={fact.label}>
            <dt>
              {fact.label}
              {fact.sub ? <small>{fact.sub}</small> : null}
            </dt>
            <dd>{formatCount(locale, fact.value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// I tipi di attività di Close che Gioia365 conosce e non importa, la stessa lista dell'importatore (nei contratti): i riepiloghi
// delle conversazioni email e gli SMS; le email, che prima erano qui, contano anche in un rapporto di prima del 22/10/2026.
const OUT_OF_SCOPE_TYPES: ReadonlySet<string> = new Set([...CLOSE_OUT_OF_SCOPE_ACTIVITY_TYPES, "Email"]);

function SummarySection({ titleId, run, summary }: { titleId: string; run: ImportRunRow; summary: ImportSummaryView }) {
  const t = useT();
  const { locale } = useLocale();
  const access = useAccess();
  const dryRun = run.mode === "dry_run";
  const when = runTitle(t, access.context.settings.timeZone, run);
  // Un rapporto c'è soltanto per un'esecuzione finita: la durata va dall'inizio alla fine, senza l'ora di adesso.
  const duration = formatDuration(t, runDuration(run, Date.parse(run.heartbeatAt)));
  const { leads, calls, notes, events, opportunities, tasks, appointments, staged, notImported, anomalies } = summary;
  const missing = anomalies.missing_in_source ?? 0;
  // Una prova dopo un'importazione: lo storico che conta è soltanto quello nuovo.
  const again = leads.update + leads.unchanged > 0;

  const leadFacts: Fact[] = [
    { label: t("closeImport.summary.leads.create"), value: leads.create, sub: t(dryRun ? "closeImport.summary.leads.createSubDryRun" : "closeImport.summary.leads.createSubApply") },
    { label: t("closeImport.summary.leads.update"), value: leads.update, sub: t("closeImport.summary.leads.updateSub") },
    { label: t("closeImport.summary.leads.unchanged"), value: leads.unchanged, sub: t("closeImport.summary.leads.unchangedSub") },
    { label: t("closeImport.summary.leads.skipped"), value: leads.skipped, sub: t("closeImport.summary.leads.skippedSub") },
  ];
  if (missing > 0) leadFacts.push({ label: t("closeImport.summary.leads.missing"), value: missing, sub: t("closeImport.summary.leads.missingSub") });

  const historyFacts: Fact[] = [
    {
      label: t("closeImport.summary.history.calls"),
      value: calls.create,
      sub: calls.invalid > 0 ? t("closeImport.summary.history.callsInvalid", { count: formatCount(locale, calls.invalid) }) : t("closeImport.summary.history.callsSub"),
    },
    { label: t("closeImport.summary.history.notes"), value: notes.create, sub: t("closeImport.summary.history.notesSub") },
    { label: t("closeImport.summary.history.statusChanges"), value: events.statusChanges },
    // Le email di Close entrano come storia (22/10/2026).
    { label: t("closeImport.summary.history.emails"), value: events.byType["close.email"] ?? 0, sub: t("closeImport.summary.history.emailsSub") },
    { label: t("closeImport.summary.history.other"), value: events.history, sub: t("closeImport.summary.history.otherSub") },
    // Le opportunità di Close (INGR-26): le nuove, con quelle aggiornate e quelle rimaste fuori.
    {
      label: t("closeImport.summary.history.opportunities"),
      value: opportunities.create,
      sub: t("closeImport.summary.history.opportunitiesSub", { update: formatCount(locale, opportunities.update), skipped: formatCount(locale, opportunities.skipped) }),
    },
    // I Task aperti di Close (22/10/2026): i richiami nuovi, con quelli aggiornati, chiusi perché completati in Close e rimasti fuori.
    {
      label: t("closeImport.summary.history.tasks"),
      value: tasks.create,
      sub: t("closeImport.summary.history.tasksSub", {
        update: formatCount(locale, tasks.update),
        completed: formatCount(locale, tasks.completed),
        skipped: formatCount(locale, tasks.skipped),
        tooOld: formatCount(locale, tasks.tooOld),
      }),
    },
    // Le disco e le demo storiche di Close (22/10/2026): appuntamenti già chiusi, con quelli rimasti fuori e le riunioni senza tipo.
    {
      label: t("closeImport.summary.history.appointments"),
      value: appointments.create,
      sub: t("closeImport.summary.history.appointmentsSub", {
        update: formatCount(locale, appointments.update),
        skipped: formatCount(locale, appointments.skipped),
        ambiguous: formatCount(locale, appointments.ambiguous),
      }),
    },
  ];

  // Le Smart View di Close come Smart List (22/10/2026): quante in Close, con le nuove, le aggiornate e quelle da rifare a mano.
  const { savedSearches } = summary;
  const laterFacts: Fact[] = [
    {
      label: t("closeImport.summary.later.savedSearches"),
      value: staged.savedSearches,
      sub: t("closeImport.summary.later.savedSearchesSub", {
        create: formatCount(locale, savedSearches.create),
        update: formatCount(locale, savedSearches.update),
        skipped: formatCount(locale, savedSearches.skipped),
      }),
    },
  ];

  // Email e SMS sempre (in Close possono essere zero), le conversazioni email accanto alle email quando ci sono, con la
  // stessa spiegazione e mai fra i tipi sconosciuti; poi gli altri tipi di Close in ordine, poi le registrazioni.
  const emailSub = t("closeImport.summary.notImported.emailSub");
  const emailThreads = notImported.EmailThread ?? 0;
  const otherTypes = Object.entries(notImported)
    .filter(([type, count]) => !OUT_OF_SCOPE_TYPES.has(type) && count > 0)
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0));
  // Le email entrano come storia dal 22/10/2026: fra quelle che non si importano soltanto in un rapporto di prima.
  const oldEmails = notImported.Email ?? 0;
  const notImportedFacts: Fact[] = [
    ...(oldEmails > 0 ? [{ label: t("closeImport.summary.notImported.email"), value: oldEmails, sub: emailSub }] : []),
    ...(emailThreads > 0 ? [{ label: t("closeImport.summary.notImported.emailThread"), value: emailThreads, sub: emailSub }] : []),
    { label: t("closeImport.summary.notImported.sms"), value: notImported.SMS ?? 0, sub: emailSub },
    ...otherTypes.map(([type, count]): Fact => ({ label: type, value: count, sub: t("closeImport.summary.notImported.otherSub") })),
    { label: t("closeImport.summary.notImported.recordings"), value: calls.withRecording, sub: t("closeImport.summary.notImported.recordingsSub") },
  ];

  return (
    <section className="g-ci__set" aria-labelledby={titleId}>
      <SectionTitle id={titleId} title={t("closeImport.summary.title")} sub={t(dryRun ? "closeImport.summary.subDryRun" : "closeImport.summary.subApply", { when, duration })} />
      <div className="g-ci__cards">
        <FactCard title={t("closeImport.summary.leads.title")} sub={t("closeImport.summary.leads.sub", { count: formatCount(locale, leads.total) })} facts={leadFacts} />
        <FactCard
          title={t("closeImport.summary.history.title")}
          sub={t(again ? "closeImport.summary.history.subAgain" : "closeImport.summary.history.sub")}
          facts={historyFacts}
        />
        <FactCard title={t("closeImport.summary.later.title")} sub={t("closeImport.summary.later.sub")} facts={laterFacts} />
        <FactCard title={t("closeImport.summary.notImported.title")} sub={t("closeImport.summary.notImported.sub")} facts={notImportedFacts} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Utenti di Close

type ReportUser = ImportSummaryView["users"][number];

/**
 * Chi è l'utente di Close in Gioia365 e che cosa succede ai suoi Lead (INGR-15): una persona del team, attiva, sospesa
 * (anche nata da Close) o archiviata, oppure, senza abbinamento, una persona sospesa che nasce, un autore storico o un
 * invitato. Un riepilogo salvato prima degli stati (`status` nullo) dice ciò che allora valeva: chi non era attivo non
 * teneva i Lead.
 */
function userLook(t: Translate, user: ReportUser, dryRun: boolean): { who: string; tone: "warning" | "accent" | null; effect: string } {
  // Una regola del profilo dà i suoi Lead a una persona del team (22/10/2026): chi è resta com'è, l'effetto lo dice.
  if (user.leadsTo !== null) {
    const look = userLook(t, { ...user, leadsTo: null }, dryRun);
    return { ...look, effect: t("closeImport.users.effectLeadsTo", { name: user.leadsTo.displayName }) };
  }
  if (user.membershipId === null) {
    switch (user.outsider) {
      case "suspended":
        // Nella prova a secco i Lead sarebbero suoi; in un'importazione che non l'ha fatta nascere sono entrati senza owner.
        return dryRun
          ? { who: t("closeImport.users.newSuspended"), tone: "accent", effect: t("closeImport.users.effectNewSuspended") }
          : { who: t("closeImport.users.newSuspendedApply"), tone: "accent", effect: t("closeImport.users.effectNewSuspendedLater", { name: user.closeName }) };
      case "invited":
        return { who: t("closeImport.users.invitedPill"), tone: "accent", effect: t("closeImport.users.effectInvited") };
      case "no_email":
        return { who: t("closeImport.users.historicNoEmail"), tone: "warning", effect: t("closeImport.users.effectNotLinked", { name: user.closeName }) };
      case "platform":
        return { who: t("closeImport.users.historicPlatform"), tone: "warning", effect: t("closeImport.users.effectNotLinked", { name: user.closeName }) };
      case "no_role":
        return { who: t("closeImport.users.historicNoRole"), tone: "warning", effect: t("closeImport.users.effectNotLinked", { name: user.closeName }) };
      default:
        return { who: t("closeImport.users.historic"), tone: "warning", effect: t("closeImport.users.effectNotLinked", { name: user.closeName }) };
    }
  }
  if (user.status === null && !user.active) return { who: t("closeImport.users.inactive"), tone: null, effect: t("closeImport.users.effectInactive") };
  if (user.status === "archived") return { who: t("closeImport.users.archived"), tone: null, effect: t("closeImport.users.effectArchived") };
  if (user.level !== 1) return { who: t(user.active ? "closeImport.users.leader" : "closeImport.users.inactive"), tone: null, effect: t("closeImport.users.effectLeader") };
  if (user.status === "suspended") {
    return { who: t(user.fromClose ? "closeImport.users.suspendedFromClose" : "closeImport.users.suspended"), tone: null, effect: t("closeImport.users.effectSuspended") };
  }
  return { who: t("closeImport.users.commercial"), tone: null, effect: t("closeImport.users.effectOwner") };
}

function UsersSection({ titleId, summary, dryRun }: { titleId: string; summary: ImportSummaryView; dryRun: boolean }) {
  const t = useT();
  const { locale } = useLocale();
  const counts = new Map(summary.userCounts.map((row) => [row.closeUserId, row]));
  // Gli utenti del riepilogo, poi quelli che hanno soltanto conteggi (per esempio un owner che Close non elenca più).
  const users: ReportUser[] = [
    ...summary.users,
    ...summary.userCounts
      .filter((row) => !summary.users.some((user) => user.closeUserId === row.closeUserId))
      .map(
        (row): ReportUser => ({
          closeUserId: row.closeUserId,
          closeName: row.closeUserId,
          membershipId: null,
          membershipDisplayName: null,
          active: false,
          level: null,
          decidedBy: "none",
          status: null,
          fromClose: false,
          outsider: null,
          leadsTo: null,
        }),
      ),
  ];
  const number = (value: number | undefined) => formatCount(locale, value ?? 0);
  const people = summary.closePeople;

  return (
    <section className="g-ci__set" aria-labelledby={titleId}>
      <SectionTitle id={titleId} title={t("closeImport.users.title")} sub={t("closeImport.users.intro")} />
      {people.create > 0 ? <p>{t(dryRun ? "closeImport.users.peopleDryRun" : "closeImport.users.peopleApply", { count: number(people.create) })}</p> : null}
      {people.existing > 0 ? <p className="g-ci__muted">{t("closeImport.users.peopleExisting", { count: number(people.existing) })}</p> : null}
      <div className="g-ci__table g-ci__table--users">
        <DataTable caption={t("closeImport.users.caption")} captionHidden>
          <thead>
            <tr>
              <th scope="col">{t("closeImport.users.closeUser")}</th>
              <th scope="col">{t("closeImport.users.inGioia")}</th>
              <th scope="col">{t("closeImport.users.how")}</th>
              <th scope="col" className="g-ci__num">
                {t("closeImport.users.leadsOwned")}
              </th>
              <th scope="col" className="g-ci__num">
                {t("closeImport.users.calls")}
              </th>
              <th scope="col" className="g-ci__num">
                {t("closeImport.users.notes")}
              </th>
              <th scope="col">{t("closeImport.users.effect")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const own = counts.get(user.closeUserId);
              const linked = user.membershipId !== null;
              const look = userLook(t, user, dryRun);
              return (
                <tr key={user.closeUserId}>
                  <th scope="row">{user.closeName}</th>
                  <td>
                    {linked ? (
                      <>
                        <strong>{user.membershipDisplayName ?? user.closeName}</strong>
                        <span className="g-table__sub">{look.who}</span>
                      </>
                    ) : (
                      <StatusPill tone={look.tone ?? "warning"} icon={look.tone === "accent" ? "clock" : "alert"}>
                        {look.who}
                      </StatusPill>
                    )}
                  </td>
                  <td>{t(user.decidedBy === "email" ? "closeImport.users.byEmail" : user.decidedBy === "manual" ? "closeImport.users.byHand" : "closeImport.users.none")}</td>
                  <td className="g-ci__num">{number(own?.leadsOwned)}</td>
                  <td className="g-ci__num">{number(own?.calls)}</td>
                  <td className="g-ci__num">{number(own?.notes)}</td>
                  <td>{look.effect}</td>
                </tr>
              );
            })}
            <tr>
              <th scope="row">
                <span className="g-ci__muted">{t("closeImport.users.nobody")}</span>
              </th>
              <td>—</td>
              <td>{t("closeImport.users.nobodyHow")}</td>
              <td className="g-ci__num">{number(summary.leadsWithoutOwner)}</td>
              <td className="g-ci__num">—</td>
              <td className="g-ci__num">—</td>
              <td>{t("closeImport.users.nobodyEffect")}</td>
            </tr>
          </tbody>
        </DataTable>
      </div>
      <p className="g-ci__muted">{t("closeImport.users.countsNote")}</p>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Come passano le variabili

type Band = "data" | "note" | "other";

function bandOf(variable: ImportVariable): Band {
  if (variable.destination === "note") return "note";
  if (variable.kind === "ignored" || variable.destination === "ignored" || variable.destination === "system:city") return "other";
  return "data";
}

/** Una variabile i cui valori dovrebbero diventare voci (un campo dell'azienda, i tag): soltanto lì un valore senza voce è rosso. */
function hasOptions(variable: ImportVariable): boolean {
  return variable.kind === "choice" && (variable.destination.startsWith("field:") || variable.destination === "system:tags");
}

function unmappedValues(variable: ImportVariable) {
  return hasOptions(variable) ? (variable.values ?? []).filter((value) => value.mappedTo === null) : [];
}

function destinationText(t: Translate, locale: ReturnType<typeof useLocale>["locale"], variable: ImportVariable, summary: ImportSummaryView): string {
  const { destination } = variable;
  if (destination === "note") return t("closeImport.variables.to.note", { label: variable.closeLabel });
  if (destination === "ignored" || variable.kind === "ignored") return t("closeImport.variables.to.ignored");
  if (destination.startsWith("field:")) return variable.destinationLabel ?? destination.slice("field:".length);
  if (destination === "system:tags") return t("closeImport.variables.to.tags");
  if (destination === "system:salon_name") return t("closeImport.variables.to.salonName");
  if (destination === "system:owner") return t("closeImport.variables.to.owner");
  if (destination === "system:created_at") return t("closeImport.variables.to.createdAt");
  if (destination === "system:city") return t("closeImport.variables.to.city");
  return fieldName({ t, locale, summary }, destination);
}

function ValuesCell({ variable, expanded, onToggle }: { variable: ImportVariable; expanded: boolean; onToggle: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  if (variable.destination === "system:owner") return <>{t("closeImport.variables.ownerInfo")}</>;
  if (variable.destination === "system:city") return <>{t("closeImport.variables.regionInfo")}</>;
  if (variable.kind === "ignored" || variable.destination === "ignored") return <>{t("closeImport.variables.ignored")}</>;
  if (variable.kind === "free_text" || !variable.values) {
    return (
      <span className="g-ci__free">
        <Icon name="lock" />
        <span>{t("closeImport.variables.freeText")}</span>
      </span>
    );
  }
  const options = hasOptions(variable);
  // Dove i valori diventano voci, quelli senza voce vengono per primi: con «Mostra tutti» chiuso si vedono comunque.
  const ordered = options ? [...variable.values].sort((left, right) => Number(left.mappedTo !== null) - Number(right.mappedTo !== null)) : variable.values;
  const shown = expanded ? ordered : ordered.slice(0, VALUES_SHOWN);
  const leads = (count: number) => t("closeImport.variables.leads", { count: formatCount(locale, count) });
  return (
    <>
      <ul className="g-ci__values">
        {shown.map((value) =>
          options && value.mappedTo === null ? (
            <li key={value.value} data-unmapped="true">
              <Icon name="close" />
              <span>{t("closeImport.variables.unmapped", { value: value.value, leads: leads(value.leads) })}</span>
            </li>
          ) : (
            <li key={value.value}>
              <span>
                {options && value.mappedToLabel !== null && value.mappedToLabel !== value.value
                  ? t("closeImport.variables.mapped", { value: value.value, label: value.mappedToLabel })
                  : value.value}
              </span>
              <span className="g-ci__muted">{leads(value.leads)}</span>
            </li>
          ),
        )}
      </ul>
      {ordered.length > VALUES_SHOWN ? (
        <Button variant="ghost" onClick={onToggle}>
          {expanded ? t("closeImport.variables.showFewer") : t("closeImport.variables.showAll", { count: formatCount(locale, ordered.length) })}
        </Button>
      ) : null}
      {options ? null : <p className="g-ci__muted">{t("closeImport.variables.noteValues")}</p>}
    </>
  );
}

function VariablesSection({ titleId, summary }: { titleId: string; summary: ImportSummaryView }) {
  const t = useT();
  const { locale } = useLocale();
  const checkboxId = useId();
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const unmappedRows = summary.variables.filter((variable) => unmappedValues(variable).length > 0);
  // Quante volte un valore senza corrispondenza va in una nota: la somma dei Lead di ogni valore, che non è un numero di
  // Lead (un Lead con due valori senza corrispondenza conta due).
  const unmappedNotes = unmappedRows.reduce((sum, variable) => sum + unmappedValues(variable).reduce((total, value) => total + value.leads, 0), 0);
  const rows = onlyUnmapped ? unmappedRows : summary.variables;
  const bands: Array<{ band: Band; label: string }> = [
    { band: "data", label: t("closeImport.variables.bandData") },
    { band: "note", label: t("closeImport.variables.bandNote") },
    { band: "other", label: t("closeImport.variables.bandOther") },
  ];
  const toggle = (label: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  let table;
  if (summary.variables.length === 0) {
    table = <p className="g-ci__muted">{t("closeImport.variables.empty")}</p>;
  } else if (rows.length === 0) {
    table = <p className="g-ci__muted">{t("closeImport.variables.noneUnmapped")}</p>;
  } else {
    table = (
      <div className="g-ci__table g-ci__table--vars">
        <DataTable caption={t("closeImport.variables.caption")} captionHidden>
          <thead>
            <tr>
              <th scope="col">{t("closeImport.variables.closeField")}</th>
              <th scope="col">{t("closeImport.variables.inGioia")}</th>
              <th scope="col">{t("closeImport.variables.values")}</th>
              <th scope="col" className="g-ci__num">
                {t("closeImport.variables.filled")}
              </th>
            </tr>
          </thead>
          {bands.map(({ band, label }) => {
            const inBand = rows.filter((variable) => bandOf(variable) === band);
            if (inBand.length === 0) return null;
            return (
              <tbody key={band}>
                <tr className="g-ci__band">
                  <th scope="colgroup" colSpan={4}>
                    {label}
                  </th>
                </tr>
                {inBand.map((variable) => (
                  <tr key={`${variable.closeLabel}|${variable.destination}`} data-unmapped={unmappedValues(variable).length > 0 ? "true" : undefined}>
                    <th scope="row">{variable.closeLabel}</th>
                    <td>{destinationText(t, locale, variable, summary)}</td>
                    <td>
                      <ValuesCell variable={variable} expanded={expanded.has(variable.closeLabel)} onToggle={() => toggle(variable.closeLabel)} />
                    </td>
                    <td className="g-ci__num">{formatCount(locale, variable.filled)}</td>
                  </tr>
                ))}
              </tbody>
            );
          })}
        </DataTable>
      </div>
    );
  }

  return (
    <section className="g-ci__set" aria-labelledby={titleId}>
      <SectionTitle id={titleId} title={t("closeImport.variables.title")} sub={t("closeImport.variables.intro")} />
      <p className="g-ci__sumline">
        <Icon name={unmappedRows.length > 0 ? "info" : "check"} />
        <span>
          {unmappedRows.length === 0
            ? t("closeImport.variables.allMapped")
            : unmappedRows.length === 1
              ? t("closeImport.variables.unmappedOne", { notes: formatCount(locale, unmappedNotes) })
              : t("closeImport.variables.unmappedMany", { count: formatCount(locale, unmappedRows.length), notes: formatCount(locale, unmappedNotes) })}
        </span>
      </p>
      <Checkbox id={checkboxId} label={t("closeImport.variables.onlyUnmapped")} checked={onlyUnmapped} onChange={setOnlyUnmapped} />
      {table}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Da controllare

function CheckSection({ titleId, run, summary }: { titleId: string; run: ImportRunRow; summary: ImportSummaryView }) {
  const t = useT();
  const groups = useMemo(() => anomalyGroups(summary.anomalies), [summary.anomalies]);
  const levels: Array<{ level: AnomalyLevel; title: string; sub: string }> = [
    { level: "look", title: t("closeImport.check.look"), sub: t("closeImport.check.lookSub") },
    { level: "know", title: t("closeImport.check.know"), sub: t("closeImport.check.knowSub") },
  ];
  return (
    <section className="g-ci__set" aria-labelledby={titleId}>
      <SectionTitle id={titleId} title={t("closeImport.check.title")} sub={t(run.mode === "dry_run" ? "closeImport.check.introDryRun" : "closeImport.check.introApply")} />
      {levels.map(({ level, title, sub }) => {
        const inLevel = groups.filter((group) => group.level === level);
        const withCases = inLevel.filter((group) => group.count > 0);
        const none = inLevel.filter((group) => group.count === 0);
        return (
          <div key={level} className="g-ci__level">
            <h3>{title}</h3>
            <p className="g-ci__muted">{sub}</p>
            {withCases.length > 0 ? (
              <ul className="g-ci__groups">
                {withCases.map((group) => (
                  <AnomalyGroupBlock key={group.code} group={group} run={run} summary={summary} />
                ))}
              </ul>
            ) : null}
            {none.length > 0 ? <p className="g-ci__muted">{t("closeImport.check.noCases", { names: none.map((group) => anomalyName(t, group)).join(", ") })}</p> : null}
          </div>
        );
      })}
    </section>
  );
}

function AnomalyGroupBlock({ group, run, summary }: { group: AnomalyGroup; run: ImportRunRow; summary: ImportSummaryView }) {
  const t = useT();
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const headRef = useRef<HTMLButtonElement>(null);
  const name = anomalyName(t, group);
  return (
    <li className="g-ci__group" data-level={group.level}>
      <h4>
        <button ref={headRef} type="button" className="g-ci__group-head" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen((value) => !value)}>
          <span>{t("closeImport.check.group", { name, count: formatCount(locale, group.count) })}</span>
          <Icon name={open ? "up" : "down"} />
        </button>
      </h4>
      <div className="g-ci__group-body" id={bodyId} hidden={!open}>
        <p>
          <strong>{t("closeImport.check.what")}</strong>
          <span>{anomalyWhat(t, group)}</span>
        </p>
        {open ? <AnomalyCases group={group} name={name} run={run} summary={summary} headRef={headRef} /> : null}
      </div>
    </li>
  );
}

function AnomalyCases({ group, name, run, summary, headRef }: { group: AnomalyGroup; name: string; run: ImportRunRow; summary: ImportSummaryView; headRef: RefObject<HTMLButtonElement | null> }) {
  const t = useT();
  const { locale } = useLocale();
  const tab = useTab();
  const cases = useInfiniteQuery(closeImportAnomaliesQuery(tab, run.id, group.code));
  if (!cases.data) {
    if (cases.isError) {
      return (
        <div className="g-ci__problem">
          <Reason tone="warning" role="alert">
            {messageForError(t, cases.error)}
          </Reason>
          <Button onClick={() => void cases.refetch()}>{t("common.retry")}</Button>
        </div>
      );
    }
    return <p className="g-ci__muted">{t("common.loading")}</p>;
  }
  const items = cases.data.pages.flatMap((page) => page.items);
  const total = cases.data.pages.at(-1)?.total ?? items.length;
  if (items.length === 0) return <p className="g-ci__muted">{t("closeImport.check.empty")}</p>;
  const ctx = { t, locale, summary };
  const left = Math.max(0, total - items.length);

  async function more() {
    // Spento con `aria-disabled`, non `disabled`: il fuoco resta sul pulsante mentre arriva la pagina.
    if (cases.isFetchingNextPage) return;
    const result = await cases.fetchNextPage();
    // Finiti i casi il pulsante sparisce: il fuoco torna al titolo del gruppo invece di perdersi.
    if (!result.hasNextPage) headRef.current?.focus();
  }

  const noLead = (code: string) => (code === "no_phone" ? t("closeImport.check.notImported") : run.mode === "dry_run" ? t("closeImport.check.notYet") : "—");
  const footer = group.code === "author_not_linked" ? t("closeImport.check.perUser") : left === 0 ? t("closeImport.check.all", { total: formatCount(locale, total) }) : t("closeImport.check.shown", { shown: formatCount(locale, items.length), total: formatCount(locale, total) });

  return (
    <>
      <div className="g-ci__table">
        <DataTable caption={t("closeImport.check.caption", { name })} captionHidden>
          <thead>
            <tr>
              <th scope="col">{t("closeImport.check.ref")}</th>
              <th scope="col">{t("closeImport.check.detail")}</th>
              <th scope="col">{t("closeImport.check.inGioia")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`${item.resource}|${item.externalId}|${index}`}>
                <th scope="row">
                  <span className="g-ci__ref">{anomalyReference(t, item)}</span>
                </th>
                <td>{describeAnomaly(ctx, item)}</td>
                <td>
                  {item.leadId ? (
                    <Link to="/lead/$leadId" params={{ leadId: item.leadId }} className="g-ci__open">
                      {t("closeImport.check.open")}
                    </Link>
                  ) : (
                    <span className="g-ci__muted">{noLead(item.code)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </div>
      {/* Una pagina dopo che non arriva: lo si dice qui, e «Mostra altri» la chiede di nuovo. */}
      {cases.isFetchNextPageError ? (
        <Reason tone="warning" role="alert">
          {messageForError(t, cases.error)}
        </Reason>
      ) : null}
      <p className="g-ci__more">
        <span className="g-ci__muted">{footer}</span>
        {cases.hasNextPage ? (
          <Button variant="ghost" onClick={() => void more()} aria-disabled={cases.isFetchingNextPage || undefined}>
            {t("closeImport.check.more", { count: formatCount(locale, Math.min(10, left)) })}
          </Button>
        ) : null}
      </p>
    </>
  );
}
